import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { useFreecodeContextRun, useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';
import { Modal, Header, Body, Footer, FooterButton } from '../modalElements';
import Path from 'routes/Path';
import services from 'api/services';

const { FreecodeSubmitService, TicketService } = services;

// Submit modal intended for use with code tests
export default function SubmitFreecodeModal({ isOpen = true }) {
  const { setDisplaySubmit } = useFreecodeContextLayout();
  const { ticket, languageData, setTicket } = useFreecodeContextData();
  const { codeSolRef, languageRef } = useFreecodeContextRun();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [extendDeadlineMessage, setExtendDeadlineMessage] = useState('');
  const [cancelDisabled, setCancelDisabled] = useState(true);

  const displayExtendOption = moment(ticket.deadlineAt).diff(Date.now(), 'minutes') < 5 && !ticket.extendedDeadline;

  const onCancel = () => setDisplaySubmit(false);

  const onSubmit = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);

    const doSubmit = async () => {
      try {
        let timeout = setTimeout(() => navigate(Path.candidateTicketsPage), 5000);
        await FreecodeSubmitService.submitFreecode({
          ticket: ticket._id,
          code: codeSolRef.current,
          language: languageRef.current,
          languageVersion: languageData[languageRef.current].CurrentVersion,
        });
        clearTimeout(timeout);
        navigate(Path.candidateTicketsPage);
      } catch (error) {
        toast.error(error?.message || 'Error submitting test');
      } finally {
        setIsLoading(false);
      }
    };

    doSubmit();
  }, [codeSolRef, isLoading, languageData, languageRef, navigate, ticket._id]);

  useEffect(() => {
    const updateTimeLeft = () => {
      const deadline = ticket.extendedDeadline || ticket.deadlineAt;
      let secondsLeft = moment(deadline).diff(Date.now(), 'seconds') + ticket.diffToServerTimeInSeconds;

      if (ticket.extendedDeadline && secondsLeft <= 0) {
        // Force submit if extended deadline has passed
        onSubmit();
      } else if (!ticket.extendedDeadline && secondsLeft <= 0) {
        if (secondsLeft < -20) onSubmit();
        setCancelDisabled(true);
        setExtendDeadlineMessage(`${20 + secondsLeft} seconds left to extend deadline before auto submit`);
      } else {
        setCancelDisabled(false);
      }
    };

    updateTimeLeft();
    const timeLeftInterval = setInterval(updateTimeLeft, 1000);
    return () => {
      clearInterval(timeLeftInterval);
    };
  }, [onSubmit, ticket.deadlineAt, ticket.extendedDeadline, ticket?.diffToServerTimeInSeconds]);

  const extendDeadline = () => {
    if (isLoading) return;
    setIsLoading(true);
    setExtendDeadlineMessage('');
    const doExtendDeadline = async () => {
      try {
        const ticketRes = await TicketService.extendTicketDeadline(ticket._id, {
          code: codeSolRef.current,
          language: languageRef.current,
          languageVersion: languageData[languageRef.current].CurrentVersion,
        });
        if (!ticketRes?._id || !ticketRes?.extendedDeadline)
          throw new Error('Extend deadline returned invalud response');
        if (ticketRes.serverTime) {
          const diffToServerTimeInSeconds = moment(new Date()).diff(ticketRes.serverTime, 'seconds');
          ticketRes.diffToServerTimeInSeconds = diffToServerTimeInSeconds;
        }
        setTicket(ticketRes);
        onCancel();
      } catch (error) {
        toast.error(error?.message || 'Error extending deadline');
      } finally {
        setIsLoading(false);
      }
    };

    doExtendDeadline();
  };

  return (
    <Modal isOpen={isOpen}>
      <Header>Submit</Header>
      <Body loading={isLoading}>
        {!extendDeadlineMessage && (
          <p>
            Are you sure you want to submit your solution? Once submitted, you will not be able to continue working on
            your code.
          </p>
        )}
        {displayExtendOption && (
          <p>
            You can extend your deadline and continue coding after submit (the official result will be for the code
            written within the time limit, but the test sender will also see the result after the time limit)
          </p>
        )}
        {extendDeadlineMessage && <p>{extendDeadlineMessage}</p>}
      </Body>
      <Footer>
        {!cancelDisabled && <FooterButton onClick={onCancel} label="Cancel" disabled={isLoading} />}
        {displayExtendOption && <FooterButton onClick={extendDeadline} label="Extend Deadline" disabled={isLoading} />}
        {!extendDeadlineMessage && <FooterButton onClick={onSubmit} label="Submit" disabled={isLoading} />}
      </Footer>
    </Modal>
  );
}
